import { swRequest } from "@sourcewhale/core";

// Call action constants
export const DIALLER_CALL_ACTIONS = {
  START_RECORDING: "start-recording",
  PAUSE_RECORDING: "pause-recording",
  RESUME_RECORDING: "resume-recording",
  STOP_RECORDING: "stop-recording",
  DROP_VOICEMAIL: "drop-voicemail",
};

/**
 * Get Twilio token for making calls
 * @param {Object} [params] - Token parameters
 * @param {boolean} [params.allowIncoming=true] - Whether to allow incoming calls
 * @returns {Promise<{token: string, expires_at: string}>} The Twilio authentication token
 */
export const diallerGetToken = ({ allowIncoming = true } = {}) =>
  swRequest({
    path: `/api/dialler/create_token`,
    method: "GET",
    params: { allowIncoming },
  });

/**
 * Create a new outbound call
 * @param {Object} params - Call parameters
 * @param {string} params.phoneNumber - Phone number to call (required)
 * @param {string} [params.candidateId] - ID of the candidate being called
 * @param {string} [params.todoId] - ID of the related todo item
 * @returns {Promise<{callId: string, status: string}>} Call creation response
 */
export const diallerCreateCall = ({ phoneNumber, candidateId, todoId }) => {
  if (!phoneNumber) {
    throw new Error("phoneNumber is required for diallerCreateCall");
  }

  return swRequest({
    path: `/api/dialler/calls`,
    method: "POST",
    body: {
      phoneNumber,
      candidateId,
      todoId,
      direction: "outbound",
    },
  });
};

/**
 * Modify an active call (record, pause, drop voicemail, etc.)
 * @param {Object} params - Modification parameters
 * @param {string} params.callSid - The Twilio call SID (required)
 * @param {string} params.action - Action to perform (required)
 * @param {string} [params.direction="outbound"] - Call direction
 * @param {string} [params.voicemailId] - ID of voicemail to drop
 * @returns {Promise<Object>} Modification response
 */
export const diallerModifyCall = ({
  callSid,
  action,
  direction = "outbound",
  voicemailId,
}) => {
  if (!callSid || !action) {
    throw new Error("callSid and action are required for diallerModifyCall");
  }

  const body = {
    CallSid: callSid,
    action,
    direction,
  };

  if (voicemailId) {
    body.voicemailId = voicemailId;
  }

  return swRequest({
    path: `/api/dialler/modify_call`,
    method: "POST",
    body,
  });
};

/**
 * Log call outcome
 * @param {Object} params - Outcome parameters
 * @param {string} params.callSid - The Twilio call SID (required)
 * @param {string} params.outcomeId - ID of the outcome (required)
 * @param {string} [params.note] - Optional note about the call
 * @param {string} [params.direction="outbound"] - Call direction
 * @returns {Promise<{success: boolean, message: string}>} Outcome logging response
 */
export const diallerLogCallOutcome = ({
  callSid,
  outcomeId,
  note,
  direction = "outbound",
}) => {
  if (!callSid || !(note || outcomeId)) {
    throw new Error(
      "callSid and either note or outcomeId are required for diallerLogCallOutcome",
    );
  }

  return swRequest({
    path: `/api/dialler/log_outcome`,
    method: "POST",
    body: {
      CallSid: callSid,
      outcomeId,
      note: note || "",
      direction,
    },
  });
};

/**
 * Get available voicemails
 * @param {Object} [params] - Query parameters
 * @param {string} [params.kind="message"] - Type of voicemails to retrieve
 * @param {boolean} [params.includeUrl=false] - Whether to include presigned URLs
 * @returns {Promise<{items: Array<{id: string, name: string, url?: string}>>} Response with voicemails
 */
export const diallerGetVoicemails = ({
  kind = "message",
  includeUrl = false,
} = {}) =>
  swRequest({
    path: `/api/dialler/voicemails`,
    method: "POST",
    body: {
      action: "get",
      data: { kind, includeUrl },
    },
  });

/**
 * Perform end-of-call checks and logging
 * @param {Object} params - Call end parameters
 * @param {string} params.callId - The call ID (required)
 * @param {string} [params.direction="outbound"] - Call direction
 * @param {string} [params.candidateId] - Associated candidate ID
 * @param {string} [params.todoId] - Associated todo ID
 * @returns {Promise<{success: boolean}>} Check completion response
 */
export const diallerEndCallChecks = ({
  callId,
  direction = "outbound",
  candidateId,
  todoId,
}) => {
  if (!callId) {
    throw new Error("callId is required for diallerEndCallChecks");
  }

  return swRequest({
    path: `/api/dialler/end_call_checks`,
    method: "POST",
    body: {
      callId,
      direction,
      candidateId,
      todoId,
    },
  });
};

export const diallerParticipants = (body) =>
  swRequest({
    path: `/api/dialler/participants`,
    method: "POST",
    body,
  });

export const diallerModifyCandidate = (body) =>
  swRequest({
    path: `/api/dialler/modify_candidate`,
    method: "POST",
    body,
  });
